import { useState } from 'react';
import { useI18n } from '../../i18n/i18n.jsx';
import Container from './Container';
import Section from './Section';
import SectionTitle from './SectionTitle';

const PartnerCard = ({ partner, index, t }) => {
  const [imageError, setImageError] = useState(false);

  return (
    <a
      href={partner.website}
      target="_blank"
      rel="noopener noreferrer"
      className="group relative flex-shrink-0 w-48 md:w-auto p-4 md:p-6 bg-white dark:bg-neutral-800 rounded-xl border border-neutral-200 dark:border-neutral-700 hover:border-accent dark:hover:border-accent/50 transition-all duration-300 hover:shadow-xl hover:-translate-y-1 md:animate-fade-in-up"
      style={{ animationDelay: `${index * 100}ms` }}
    >
      {/* Logo Container - Compact and elegant */}
      <div className="w-full h-24 md:h-32 flex items-center justify-center bg-neutral-50 dark:bg-neutral-900/30 rounded-lg overflow-hidden relative mb-3 md:mb-4">
        {!imageError ? (
          <img
            src={partner.logo}
            alt={t(`partners.items.${partner.nameKey}`)}
            className="max-w-[80%] max-h-[70%] object-contain opacity-70 group-hover:opacity-100 group-hover:scale-110 transition-all duration-300 filter grayscale group-hover:grayscale-0"
            onError={() => setImageError(true)}
          />
        ) : (
          <div className="text-xl md:text-2xl font-bold text-primary/60 dark:text-accent/60 group-hover:text-primary dark:group-hover:text-accent transition-colors duration-300">
            {partner.fallbackInitial || partner.name.charAt(0)}
          </div>
        )}
      </div>
      
      {/* Partner Info - Compact */}
      <div className="text-center">
        <h3 className="text-xs md:text-sm font-semibold text-primary dark:text-white mb-1 group-hover:text-accent transition-colors duration-300 line-clamp-1">
          {t(`partners.items.${partner.nameKey}`)}
        </h3>
        <p className="text-[10px] md:text-xs text-neutral-500 dark:text-neutral-400 line-clamp-2 leading-tight">
          {t(`partners.items.${partner.descriptionKey}`)}
        </p>
      </div>

      {/* Subtle hover indicator */}
      <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
        <svg
          className="w-4 h-4 text-accent"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"
          />
        </svg>
      </div>
    </a>
  );
};

const Partners = () => {
  const { t } = useI18n();

  // Partner data - educational centers in Ashgabat
  const partners = [
    {
      id: 'zehin-yoly',
      name: 'Zehin Ýoly',
      nameKey: 'zehinYoly',
      description: 'Language and Math courses for children and adults',
      descriptionKey: 'zehinYolyDesc',
      website: 'https://trade.com.tm/companies/zehin-%C3%BDoly-185/',
      logo: '/assets/partners/zehin-yoly.png',
      fallbackInitial: 'ZY'
    },
    {
      id: 'qsi-ashgabat',
      name: 'QSI Ashgabat',
      nameKey: 'qsiAshgabat',
      description: 'International School',
      descriptionKey: 'qsiAshgabatDesc',
      website: 'https://ashgabat.qsi.org',
      logo: '/assets/partners/qsi-ashgabat.png',
      fallbackInitial: 'QSI'
    },
    {
      id: 'gujurly',
      name: 'Gujurly',
      nameKey: 'gujurly',
      description: 'Educational Center',
      descriptionKey: 'gujurlyDesc',
      website: 'https://gujurly.edu.tm/',
      logo: '/assets/partners/gujurly.png',
      fallbackInitial: 'GJ'
    }
  ];

  return (
    <Section className="py-12 md:py-16 bg-white dark:bg-neutral-900 border-b border-neutral-200 dark:border-neutral-800">
      <Container>
        <div className="text-center mb-8 md:mb-12">
          <p className="text-sm md:text-base font-semibold text-accent dark:text-accent-light uppercase tracking-wider mb-2">
            {t('partners.title')}
          </p>
          <p className="text-xs md:text-sm text-neutral-500 dark:text-neutral-400">
            {t('partners.subtitle')}
          </p>
        </div>
        
        <div className="relative">
          {/* Gradient overlays for fade effect */}
          <div className="absolute left-0 top-0 bottom-0 w-20 md:w-32 bg-gradient-to-r from-white via-white/80 to-transparent dark:from-neutral-900 dark:via-neutral-900/80 z-10 pointer-events-none" />
          <div className="absolute right-0 top-0 bottom-0 w-20 md:w-32 bg-gradient-to-l from-white via-white/80 to-transparent dark:from-neutral-900 dark:via-neutral-900/80 z-10 pointer-events-none" />
          
          {/* Partners Grid - Horizontal Scroll on mobile, Grid on desktop */}
          <div className="flex md:grid md:grid-cols-3 gap-6 md:gap-8 overflow-x-auto md:overflow-visible pb-4 md:pb-0 scrollbar-hide justify-center">
            {partners.map((partner, index) => (
              <PartnerCard
                key={partner.id}
                partner={partner}
                index={index}
                t={t}
              />
            ))}
          </div>
        </div>
      </Container>
    </Section>
  );
};

export default Partners;
